import { Component, OnDestroy, OnInit } from '@angular/core';
import { Subscription } from 'rxjs';
import { AuthService } from 'common/services/auth.service';
import { RealtimeService } from 'common/services/realtime.service';

type IslandPhase = 'hidden' | 'in' | 'open' | 'exit';

interface IslandNotice {
  message: string;
  link: string | null;
}

interface MemberRequestCreated {
  id?: string;
  type?: string;
  memberName?: string;
}

const TYPE_LABELS: Record<string, string> = {
  freeze: 'Freeze',
  unfreeze: 'Unfreeze',
  deactivate: 'Deactivate',
  package_change: 'Package change',
  profile_correction: 'Profile correction',
  trainer_change: 'Trainer change',
};

const CHANNEL = 'member-requests';
const CREATED_EVENT = 'member-request.created';
const HOLD_MS = 4000;
const WELCOME_KEY = 'tecfit-welcome-shown';

@Component({
  selector: 'app-member-requests-island',
  template: `
    <ng-container *ngIf="phase !== 'hidden'">
      <a *ngIf="link; else plain" class="member-requests-island"
        [class.is-open]="phase === 'open'"
        [class.is-exit]="phase === 'exit'"
        [routerLink]="link"
        [attr.aria-label]="message">
        <span class="member-requests-island__body">
          <span class="member-requests-island__bell" aria-hidden="true"><i class="fa fa-bell"></i></span>
          <span class="member-requests-island__text">{{ message }}</span>
        </span>
      </a>
      <ng-template #plain>
        <div class="member-requests-island" [class.is-open]="phase === 'open'"
          [class.is-exit]="phase === 'exit'"
          role="status" [attr.aria-label]="message">
          <span class="member-requests-island__body">
            <span class="member-requests-island__bell" aria-hidden="true"><i class="fa fa-bell"></i></span>
            <span class="member-requests-island__text">{{ message }}</span>
          </span>
        </div>
      </ng-template>
    </ng-container>
  `,
  styles: [`
    :host { display: contents; }
    .member-requests-island {
      position: fixed;
      top: 83px;
      left: 50%;
      z-index: 1000;
      display: flex;
      align-items: center;
      justify-content: center;
      width: 10px;
      height: 10px;
      overflow: hidden;
      box-sizing: border-box;
      border: 0 solid #fff;
      border-radius: 999px;
      background: #1a3f8f;
      color: #fff;
      text-decoration: none;
      pointer-events: none;
      opacity: 0;
      transform: translate(-50%, -140px);
      box-shadow: none;
    }
    .member-requests-island.is-open {
      opacity: 1;
      pointer-events: auto;
      animation: island-enter 0.9s cubic-bezier(0.22, 1.2, 0.32, 1) both;
    }
    .member-requests-island.is-exit {
      opacity: 1;
      pointer-events: none;
      animation: island-exit 0.9s cubic-bezier(0.4, 0, 0.2, 1) both;
    }
    .member-requests-island__body {
      display: flex;
      align-items: center;
      gap: 12px;
      max-width: 100%;
      padding: 0;
      opacity: 0;
    }
    .member-requests-island.is-open .member-requests-island__body {
      animation: island-message-in 0.9s ease forwards;
    }
    .member-requests-island.is-exit .member-requests-island__body {
      animation: island-message-out 0.22s ease forwards;
    }
    .member-requests-island__bell {
      flex: 0 0 auto;
      width: 26px;
      height: 26px;
      border-radius: 999px;
      display: inline-flex;
      align-items: center;
      justify-content: center;
      background: #f5a524;
      color: #fff;
      font-size: 13px;
      box-shadow:
        0 4px 8px rgba(16, 40, 90, 0.22),
        inset 0 1px 0 rgba(255, 255, 255, 0.45);
    }
    .member-requests-island__text {
      font-size: 15px;
      font-weight: 700;
      line-height: 1.2;
      letter-spacing: -0.01em;
      white-space: nowrap;
    }
    .member-requests-island:hover { color: #fff; }
    .member-requests-island:focus-visible {
      outline: 2px solid #f5a524;
      outline-offset: 3px;
    }
    @media (prefers-reduced-motion: reduce) {
      .member-requests-island.is-open,
      .member-requests-island.is-exit,
      .member-requests-island__body {
        animation: none;
      }
      .member-requests-island.is-open {
        width: max-content;
        max-width: calc(100vw - 48px);
        height: 56px;
        padding: 0 18px 0 10px;
        border-width: 4px;
        border-radius: 999px;
        opacity: 1;
        transform: translate(-50%, 0);
        box-shadow: 0 14px 28px rgba(15, 23, 42, 0.22);
      }
      .member-requests-island.is-open .member-requests-island__body { opacity: 1; }
    }
    @keyframes island-enter {
      0% {
        width: 10px;
        height: 10px;
        padding: 0;
        border-width: 0;
        border-radius: 999px;
        opacity: 1;
        box-shadow: none;
        transform: translate(-50%, -140px);
      }
      42% {
        width: 10px;
        height: 10px;
        padding: 0;
        border-width: 0;
        border-radius: 999px;
        opacity: 1;
        box-shadow: none;
        transform: translate(-50%, 0);
      }
      78% {
        width: max-content;
        height: 60px;
        padding: 0 20px 0 12px;
        border-width: 4px;
        border-radius: 999px;
        box-shadow: 0 16px 30px rgba(15, 23, 42, 0.2);
        transform: translate(-50%, 0);
      }
      100% {
        width: max-content;
        height: 56px;
        padding: 0 18px 0 10px;
        border-width: 4px;
        border-radius: 999px;
        box-shadow: 0 14px 28px rgba(15, 23, 42, 0.22);
        transform: translate(-50%, 0);
      }
    }
    @keyframes island-exit {
      0% {
        width: max-content;
        height: 56px;
        padding: 0 18px 0 10px;
        border-width: 4px;
        border-radius: 999px;
        opacity: 1;
        box-shadow: 0 14px 28px rgba(15, 23, 42, 0.22);
        transform: translate(-50%, 0);
      }
      46% {
        width: 10px;
        height: 10px;
        padding: 0;
        border-width: 0;
        border-radius: 999px;
        opacity: 1;
        box-shadow: none;
        transform: translate(-50%, 0);
      }
      100% {
        width: 10px;
        height: 10px;
        padding: 0;
        border-width: 0;
        border-radius: 999px;
        opacity: 1;
        box-shadow: none;
        transform: translate(-50%, -140px);
      }
    }
    @keyframes island-message-in {
      0%, 40% { opacity: 0; }
      100% { opacity: 1; }
    }
    @keyframes island-message-out {
      0% { opacity: 1; }
      100% { opacity: 0; }
    }
  `],
  providers: [RealtimeService],
})
export class MemberRequestsIslandComponent implements OnInit, OnDestroy {
  message = '';
  link: string | null = null;
  phase: IslandPhase = 'hidden';
  private queue: IslandNotice[] = [];
  private statusSub: Subscription | null = null;
  private subscribed = false;
  private timers: number[] = [];
  private frames: number[] = [];
  private readonly reduceMotion =
    typeof window !== 'undefined' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  constructor(private readonly realtime: RealtimeService) {}

  ngOnInit() {
    this.showWelcome();
    this.connectRealtime();
  }

  ngOnDestroy() {
    this.clearTimers();
    this.statusSub?.unsubscribe();
    this.realtime.disconnect();
  }

  private showWelcome() {
    let user: { name?: string; username?: string; _id?: string } | null = null;
    let token = '';
    try {
      user = AuthService.getLoggedInUser();
      token = String(AuthService.getToken() || '');
    } catch {
      user = null;
    }
    const name = String(user?.name || user?.username || '').trim();
    const sessionKey = token || String(user?._id || user?.username || '');
    if (!name || !sessionKey || sessionStorage.getItem(WELCOME_KEY) === sessionKey) return;
    sessionStorage.setItem(WELCOME_KEY, sessionKey);
    this.later(2000, () => {
      this.enqueue({ message: `Welcome, ${name}`, link: null });
    });
  }

  private connectRealtime() {
    let role: { privileges?: { pageType?: string }[] } | null = null;
    let user: { branch?: string } | null = null;
    try {
      role = AuthService.getLoggedInRole();
      user = AuthService.getLoggedInUser();
    } catch {
      return;
    }
    const allowed = !!role?.privileges?.some(
      (permission) => permission?.pageType === 'Member Requests',
    );
    const branchId = user?.branch;
    if (!allowed || !branchId) return;

    this.statusSub = this.realtime.status.subscribe((status) => {
      if (status === 'connected' && !this.subscribed) {
        this.subscribed = true;
        void this.realtime.subscribe(CHANNEL, branchId);
      }
    });
    this.realtime.connect(branchId);
    this.realtime.on<MemberRequestCreated>(CREATED_EVENT, (payload) =>
      this.onRequestCreated(payload),
    );
  }

  private onRequestCreated(payload: MemberRequestCreated) {
    const id = String(payload?.id ?? '').trim();
    const memberName = String(payload?.memberName ?? '').trim();
    const type = String(payload?.type ?? '').trim();
    if (!id || !memberName) return;
    const label = TYPE_LABELS[type] || type;
    this.enqueue({
      message: `${memberName} · ${label}`,
      link: `/member-requests/${id}`,
    });
  }

  private enqueue(notice: IslandNotice) {
    if (this.phase !== 'hidden') {
      this.queue.push(notice);
      return;
    }
    this.show(notice);
  }

  private show(notice: IslandNotice) {
    this.message = notice.message;
    this.link = notice.link;
    if (this.reduceMotion) {
      this.phase = 'open';
      this.later(HOLD_MS, () => this.dismiss());
      return;
    }
    this.phase = 'in';
    this.frame(() => {
      this.frame(() => {
        if (this.phase !== 'in') return;
        this.phase = 'open';
        this.later(HOLD_MS, () => this.dismiss());
      });
    });
  }

  private dismiss() {
    if (this.reduceMotion || this.phase === 'hidden' || this.phase === 'in') {
      this.phase = 'hidden';
      this.showNext();
      return;
    }
    this.phase = 'exit';
    this.later(900, () => {
      if (this.phase !== 'exit') return;
      this.phase = 'hidden';
      this.showNext();
    });
  }

  private showNext() {
    const next = this.queue.shift();
    if (next) this.show(next);
  }

  private later(ms: number, fn: () => void) {
    const id = window.setTimeout(() => {
      this.timers = this.timers.filter((timer) => timer !== id);
      fn();
    }, ms);
    this.timers.push(id);
  }

  private frame(fn: () => void) {
    const id = requestAnimationFrame(() => {
      this.frames = this.frames.filter((frame) => frame !== id);
      fn();
    });
    this.frames.push(id);
  }

  private clearTimers() {
    this.timers.forEach((id) => clearTimeout(id));
    this.timers = [];
    this.frames.forEach((id) => cancelAnimationFrame(id));
    this.frames = [];
  }
}
